import React, { useState } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Button, 
  Typography, 
  Statistic, 
  Input, 
  DatePicker,
  Space,
  Tabs,
  List,
  Avatar,
  Tag,
  Spin
} from 'antd';
import { 
  CarOutlined, 
  ShoppingCartOutlined, 
  UserOutlined, 
  SearchOutlined,
  PlusOutlined,
  EnvironmentOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import UserRating from '../components/UserRating';
import api from '../services/api';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { routeText } from '../utils/cityDisplay';

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

const Home = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('home');
  const [searchParams, setSearchParams] = useState({
    origin_city: '',
    dest_city: '',
    depart_date: dayjs().format('YYYY-MM-DD'),
    type: 'trips'
  });

  // 获取统计数据
  const { data: stats, isLoading: statsLoading } = useQuery(
    'homeStats',
    async () => {
      // 只展示真实数据。此前「注册用户 1000 / 完成订单 500」是写死的模拟数，对外展示属虚假宣传
      const [tripsRes, requestsRes] = await Promise.all([
        api.get('/trips/search?limit=1'),
        api.get('/requests/search?limit=1'),
      ]);

      return {
        totalTrips: tripsRes.data.pagination?.total || 0,
        totalRequests: requestsRes.data.pagination?.total || 0,
      };
    }
  );

  // 获取热门行程
  const { data: popularTrips, isLoading: tripsLoading } = useQuery(
    'popularTrips',
    async () => {
      const response = await api.get('/trips/search?limit=6');
      return response.data.trips;
    }
  );

  // 获取热门需求
  const { data: popularRequests, isLoading: requestsLoading } = useQuery(
    'popularRequests',
    async () => {
      const response = await api.get('/requests/search?limit=6');
      return response.data.requests;
    }
  );

  // 获取热门路线
  const { data: popularRoutes } = useQuery(
    'popularRoutes',
    async () => {
      const response = await api.get('/matching/popular-routes?limit=5');
      return response.data.routes;
    }
  );

  // 城市名须编码（含空格 / & 的英文名会截断查询串）；需求不按日期筛，不带 date
  const handleSearch = () => {
    const q = new URLSearchParams({ origin: searchParams.origin_city, dest: searchParams.dest_city });
    if (searchParams.type === 'trips') {
      q.set('date', searchParams.depart_date);
      navigate(`/trips?${q}`);
    } else {
      navigate(`/requests?${q}`);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'green',
      pending: 'orange',
      completed: 'blue',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      active: t('common:orderStatus.active'),
      pending: t('common:orderStatus.pending'),
      completed: t('common:orderStatus.completed'),
      cancelled: t('common:orderStatus.cancelled')
    };
    return texts[status] || status;
  };

  return (
    <div>
      {/* 英雄区域 */}
      <div className="hero-section">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <Title level={1} style={{ color: 'white', marginBottom: '16px' }}>
            {t('hero.title')}
          </Title>
          <Paragraph style={{ color: 'white', fontSize: '18px', marginBottom: '32px' }}>
            {t('hero.subtitle')}
          </Paragraph>
          
          {/* 搜索区域 */}
          <Card style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Tabs 
              activeKey={searchParams.type} 
              onChange={(key) => setSearchParams(prev => ({ ...prev, type: key }))}
            >
              <TabPane tab={t('search.findTrips')} key="trips">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Row gutter={16}>
                    <Col xs={24} sm={8}>
                      <Input
                        placeholder={t('search.originCity')}
                        value={searchParams.origin_city}
                        onChange={(e) => setSearchParams(prev => ({ ...prev, origin_city: e.target.value }))}
                        prefix={<EnvironmentOutlined />}
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <Input
                        placeholder={t('search.destCity')}
                        value={searchParams.dest_city}
                        onChange={(e) => setSearchParams(prev => ({ ...prev, dest_city: e.target.value }))}
                        prefix={<EnvironmentOutlined />}
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <DatePicker
                        style={{ width: '100%' }}
                        value={dayjs(searchParams.depart_date)}
                        onChange={(date) => setSearchParams(prev => ({ ...prev, depart_date: date?.format('YYYY-MM-DD') || '' }))}
                      />
                    </Col>
                  </Row>
                  <Button 
                    type="primary" 
                    size="large" 
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    block
                  >
                    {t('search.searchTrips')}
                  </Button>
                </Space>
              </TabPane>
              
              <TabPane tab={t('search.findRequests')} key="requests">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Input
                        placeholder={t('search.buyCity')}
                        value={searchParams.origin_city}
                        onChange={(e) => setSearchParams(prev => ({ ...prev, origin_city: e.target.value }))}
                        prefix={<EnvironmentOutlined />}
                      />
                    </Col>
                    <Col xs={24} sm={12}>
                      <Input
                        placeholder={t('search.deliverCity')}
                        value={searchParams.dest_city}
                        onChange={(e) => setSearchParams(prev => ({ ...prev, dest_city: e.target.value }))}
                        prefix={<EnvironmentOutlined />}
                      />
                    </Col>
                  </Row>
                  <Button 
                    type="primary" 
                    size="large" 
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    block
                  >
                    {t('search.searchRequests')}
                  </Button>
                </Space>
              </TabPane>
            </Tabs>
          </Card>
        </div>
      </div>

      {/* 统计数据：真实的在架行程 / 需求数；都为 0（开张初期）时不显示，免得首页挂着一排 0 */}
      {(statsLoading || stats?.totalTrips > 0 || stats?.totalRequests > 0) && (
      <div className="stats-section">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: '48px' }}>
            {t('stats.title')}
          </Title>
          <Row gutter={[32, 32]} justify="center">
            <Col xs={12} sm={8}>
              <div className="stat-item">
                <div className="stat-number">
                  {statsLoading ? <Spin /> : stats?.totalTrips || 0}
                </div>
                <div className="stat-label">{t('stats.trips')}</div>
              </div>
            </Col>
            <Col xs={12} sm={8}>
              <div className="stat-item">
                <div className="stat-number">
                  {statsLoading ? <Spin /> : stats?.totalRequests || 0}
                </div>
                <div className="stat-label">{t('stats.requests')}</div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      )}

      {/* 功能特色 */}
      <div style={{ padding: '80px 0', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: '48px' }}>
            {t('features.title')}
          </Title>
          <Row gutter={[32, 32]}>
            <Col xs={24} sm={8}>
              <div className="feature-card">
                <div className="feature-icon">
                  <CarOutlined />
                </div>
                <Title level={4}>{t('features.routeTitle')}</Title>
                <Paragraph>
                  {t('features.routeDesc')}
                </Paragraph>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div className="feature-card">
                <div className="feature-icon">
                  <ShoppingCartOutlined />
                </div>
                <Title level={4}>{t('features.safeTitle')}</Title>
                <Paragraph>
                  {t('features.safeDesc')}
                </Paragraph>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div className="feature-card">
                <div className="feature-icon">
                  <UserOutlined />
                </div>
                <Title level={4}>{t('features.communityTitle')}</Title>
                <Paragraph>
                  {t('features.communityDesc')}
                </Paragraph>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* 热门内容 */}
      <div style={{ padding: '80px 0', background: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <Row gutter={[32, 32]}>
            <Col xs={24} lg={12}>
              <Card 
                title={t('popular.trips')}
                extra={<Button type="link" onClick={() => navigate('/trips')}>{t('common:actions.viewMore')}</Button>}
              >
                <List
                  loading={tripsLoading}
                  dataSource={popularTrips}
                  renderItem={(trip) => (
                    <List.Item
                      actions={[
                        <Button 
                          type="primary" 
                          size="small"
                          onClick={() => navigate(`/trips/${trip.trip_id}`)}
                        >
                          {t('common:actions.viewDetail')}
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={routeText(trip, i18n.language)}
                        description={
                          <div>
                            <div>
                              <CalendarOutlined /> {dayjs(trip.depart_date).format(t('popular.dateFormat'))}
                            </div>
                            <div>
                              <UserRating rating={trip.rating} reviewCount={trip.review_count} showValue={false} />
                              <span style={{ marginLeft: '8px' }}>{trip.username}</span>
                            </div>
                            <div>
                              <Tag color={getStatusColor(trip.status)}>
                                {getStatusText(trip.status)}
                              </Tag>
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
            
            <Col xs={24} lg={12}>
              <Card 
                title={t('popular.requests')}
                extra={<Button type="link" onClick={() => navigate('/requests')}>{t('common:actions.viewMore')}</Button>}
              >
                <List
                  loading={requestsLoading}
                  dataSource={popularRequests}
                  renderItem={(request) => (
                    <List.Item
                      actions={[
                        <Button 
                          type="primary" 
                          size="small"
                          onClick={() => navigate(`/requests/${request.request_id}`)}
                        >
                          {t('common:actions.viewDetail')}
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={request.item_name}
                        description={
                          <div>
                            <div>
                              {routeText(request, i18n.language)}
                            </div>
                            <div>
                              <UserRating rating={request.rating} reviewCount={request.review_count} showValue={false} />
                              <span style={{ marginLeft: '8px' }}>{request.username}</span>
                            </div>
                            <div>
                              <Tag color="blue">¥{request.reward_amount}</Tag>
                              <Tag color={getStatusColor(request.status)}>
                                {getStatusText(request.status)}
                              </Tag>
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* 热门路线 */}
      {popularRoutes && popularRoutes.length > 0 && (
        <div style={{ padding: '80px 0', background: 'white' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
            <Title level={2} style={{ textAlign: 'center', marginBottom: '48px' }}>
              {t('popular.routes')}
            </Title>
            <Row gutter={[16, 16]}>
              {popularRoutes.map((route, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <Card size="small">
                    <div style={{ textAlign: 'center' }}>
                      <Title level={4}>
                        {routeText(route, i18n.language)}
                      </Title>
                      <div>
                        <Statistic 
                          title={t('popular.routeTrips')}
                          value={Number(route.trip_count) || 0}
                          suffix={t('common:unit.times')}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </div>
                      {/* 不显示路线「平均评分」：pg 的 AVG 返回字符串，原 .toFixed 直接抛错让整个首页白屏；
                          且新用户 rating 默认 5.00，平均值几乎恒为 5 星，误导买家 */}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      )}

      {/* CTA区域 */}
      <div style={{ 
        padding: '80px 0', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 24px' }}>
          <Title level={2} style={{ color: 'white', marginBottom: '16px' }}>
            {t('cta.title')}
          </Title>
          <Paragraph style={{ color: 'white', fontSize: '16px', marginBottom: '32px' }}>
            {t('cta.subtitle')}
          </Paragraph>
          <Space size="large">
            <Button 
              type="primary" 
              size="large"
              icon={<PlusOutlined />}
              onClick={() => navigate('/register')}
              style={{ background: 'white', color: '#1890ff', border: 'none' }}
            >
              {t('cta.register')}
            </Button>
            <Button 
              size="large"
              onClick={() => navigate('/trips')}
              style={{ color: 'white', borderColor: 'white' }}
            >
              {t('cta.browseTrips')}
            </Button>
          </Space>
        </div>
      </div>
    </div>
  );
};

export default Home;
